import createReducerMixin from "./createReducerMixin";
import {strictParseTime, timeToSpan} from "../lib/TimeUtils";

const getData = props => props.dataset || {name: null, data: []};

const SECOND = 1000,
    MINUTE = 60 * SECOND,
    HOUR = 60 * MINUTE,
    DAY = 24 * HOUR,
    MONTH = 31 * DAY,
    YEAR = 365 * DAY;

const GRANULARITIES = [
    //granularity format and unit duration
    ["minute", MINUTE],
    ["hour", HOUR],
    ["day", DAY],
    ["month", MONTH],
    ["quarter", MONTH * 3],
    ["year", YEAR]
];

const MAX_BINS = 120;
const axisKeyName = "xKey";

const useAuto = ({granularity}) => [void 0, "", "auto"].indexOf(granularity) > -1;

function calculateGranularity(state, nextProps, oldProps) {
    const emptyState = (function(s) {
        let obj = {};
        Object.keys(s).forEach(key => obj[key] = null);
        return obj;
    })(state);

    const invalidState = {...emptyState, invalid: true};

    if (!useAuto(nextProps) || !nextProps[axisKeyName]) {
        return emptyState;
    }

    //force recalculation when either xKey or granularity setting is changed
    if ([axisKeyName, "granularity"].some(key => nextProps[key] !== oldProps[key])) {
        oldProps = {};
        state = emptyState;
    }

    let dataset = getData(nextProps),
        oldDataset = getData(oldProps);

    if (dataset !== oldDataset && !state.invalid) {
        let oldData = [];

        // allow an incremental update only when it can be safely done:
        if (dataset.name === oldDataset.name) {
            oldData = getData(oldProps).data;
        }

        let newData = dataset.data.slice(oldData.length);

        let timespan = state.timespan || {start: Infinity, end: -Infinity, stampGranularity: -Infinity};

        for (let i = 0; i < newData.length; i++) {
            let event = newData[i];
            let stamps = event
                .values(nextProps[axisKeyName])
                .filter(val => val != "")
                .map(strictParseTime);
            if (stamps.length > 1 || stamps.some(time => time === null)) {
                return invalidState;
            } else if (stamps.length == 0) {
                continue;
            }

            if (timespan.stampGranularity < 0) {
                let span = event
                    .values(nextProps[axisKeyName])
                    .filter(val => val != "")
                    .map(timeToSpan)[0];

                timespan.stampGranularity = span.end - span.start;
            }

            let stamp = stamps[0];

            if (timespan.start > stamp) {
                timespan.start = stamp;
            }

            if (timespan.end < stamp) {
                timespan.end = stamp;
            }
        }

        let duration = timespan.end - timespan.start;
        let granularity = "month";

        for (let i = 0; i < GRANULARITIES.length; i++) {
            let [name, unit] = GRANULARITIES[i];
            // the later check is for preventing granularities that
            // are higher in precision than the actual timestamps
            if ((duration / unit) < MAX_BINS && unit > timespan.stampGranularity * 0.9) {
                granularity = name;
                break;
            }
        }

        state = {granularity, timespan};
    }

    return state;
}

export default function autoGranularity(WrappedComponent) {
    return class AutoGranularity extends createReducerMixin(WrappedComponent, calculateGranularity) {
        static displayName = 'AutoGranularity';

        render() {
            let props = this.props;

            if (useAuto(props)) {
                props = {...props, granularity: this.state.granularity};
            }

            return super.render(props);
        }
    };
}



// WEBPACK FOOTER //
// ./src/kpi/common/AutoGranularity.js